import { useState, type FormEvent } from "react";

interface ResetPasswordFormProps {
  loading: boolean;
  error: string;
  message: string;
  onSubmit: (password: string) => void;
}

const baseField =
  "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100";

export default function ResetPasswordForm({ loading, error, message, onSubmit }: ResetPasswordFormProps) {
  const [errors, setErrors] = useState<{ password?: string; confirmar?: string }>({});

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const password = String(formData.get("password") ?? "");
    const confirmar = String(formData.get("confirmar") ?? "");

    const next: { password?: string; confirmar?: string } = {};
    if (!password) next.password = "Ingresa la nueva contraseña.";
    else if (password.length < 8) next.password = "Mínimo 8 caracteres.";
    if (password !== confirmar) next.confirmar = "Las contraseñas no coinciden.";

    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    onSubmit(password);
  };

  const labelClass = "mb-1.5 block text-sm font-label font-medium text-foreground-700";

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
      <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
        <i className="ri-lock-2-line text-xl leading-none" aria-hidden="true"></i>
      </span>
      <h1 className="mt-4 text-xl font-heading font-bold text-foreground-950">Nueva contraseña</h1>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        Define una contraseña nueva y segura para tu cuenta de administrador.
      </p>

      <form className="mt-6 space-y-4" noValidate onSubmit={handleSubmit}>
        <div>
          <label htmlFor="password" className={labelClass}>
            Nueva contraseña
          </label>
          <input id="password" name="password" type="password" autoComplete="new-password" placeholder="Mínimo 8 caracteres" className={baseField} />
          {errors.password && <p className="mt-1 text-xs text-primary-700">{errors.password}</p>}
        </div>
        <div>
          <label htmlFor="confirmar" className={labelClass}>
            Confirmar contraseña
          </label>
          <input id="confirmar" name="confirmar" type="password" autoComplete="new-password" placeholder="Repite la contraseña" className={baseField} />
          {errors.confirmar && <p className="mt-1 text-xs text-primary-700">{errors.confirmar}</p>}
        </div>

        {error && (
          <p role="alert" className="flex items-start gap-2 rounded-md bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
            {error}
          </p>
        )}
        {message && (
          <p className="flex items-start gap-2 rounded-md bg-accent-50 px-3.5 py-3 text-sm text-accent-900">
            <i className="ri-checkbox-circle-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
            {message}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-base font-label font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-foreground-950"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base leading-none" aria-hidden="true"></i>
              Guardando…
            </>
          ) : (
            "Guardar contraseña"
          )}
        </button>
      </form>
    </div>
  );
}